/*
  Colour swatches on a product card.

  Each swatch is a real link to the variant, so it works without JavaScript.
  With JavaScript it swaps the card image in place and repoints the card's
  links at the chosen variant instead of navigating.

  The swap always runs the same sequence: fade to white, wait for the new
  image to decode, fade back in. Doing this even for cached images keeps the
  interaction consistent, so a click never feels like it did nothing.
*/
if (!customElements.get('card-swatches')) {
  const FADE_MS = 160;

  class CardSwatches extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('.card-wrapper');
      if (!this.card) return;

      this.primary = this.card.querySelector('.card__image--primary');
      this.secondary = this.card.querySelector('.card__image--secondary');
      this.links = this.card.querySelectorAll('[data-card-link]');
      this.token = 0;

      this.addEventListener('click', this.onClick.bind(this));

      // Warm the cache on hover so the click itself rarely waits on the network.
      this.addEventListener('pointerover', this.onPointerOver.bind(this));
    }

    onPointerOver(event) {
      const swatch = event.target.closest('[data-swatch-image]');
      if (!swatch || swatch.dataset.preloaded) return;

      swatch.dataset.preloaded = 'true';
      new Image().src = swatch.dataset.swatchImage;
    }

    onClick(event) {
      const swatch = event.target.closest('[data-swatch-image]');
      if (!swatch) return;

      const src = swatch.dataset.swatchImage;

      // No image for this variant, so let the link navigate as normal.
      if (!src || !this.primary) return;

      event.preventDefault();
      if (swatch.hasAttribute('aria-current')) return;

      this.querySelectorAll('[aria-current]').forEach((el) => el.removeAttribute('aria-current'));
      swatch.setAttribute('aria-current', 'true');

      // The ring means "you chose this", so it only appears once a choice has
      // actually been made. On a card nobody has touched, no swatch is ringed
      // even though one is marked current for the variant being shown.
      this.dataset.chosen = 'true';

      const url = swatch.dataset.swatchUrl;
      if (url) this.links.forEach((link) => (link.href = url));

      this.swap(src);
    }

    swap(src) {
      // Guards against a second click landing while the first is still running.
      const token = ++this.token;

      this.card.classList.add('is-swapping');

      const faded = new Promise((resolve) => setTimeout(resolve, FADE_MS));
      const loaded = new Promise((resolve) => {
        const preload = new Image();
        preload.onload = resolve;
        preload.onerror = resolve;
        preload.src = src;
      });

      Promise.all([faded, loaded]).then(() => {
        if (token !== this.token) return;

        // The hover image belongs to the old colour, so drop it.
        if (this.secondary) {
          this.secondary.remove();
          this.secondary = null;
        }

        this.primary.srcset = '';
        this.primary.src = src;

        // Two frames, so the new image has painted before we fade back in.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (token === this.token) this.card.classList.remove('is-swapping');
          });
        });
      });
    }
  }

  customElements.define('card-swatches', CardSwatches);
}
